import { throttle, debounce } from 'lodash-es';
import { shallowRef, reactive, watchEffect, onUnmounted } from 'vue';
export interface Rect {
    left: number;
    top: number;
    right: number;
    bottom: number;
    width: number;
    height: number;
    x: number;
    y: number;
}

interface Option {
    wait?: number;
    type?: 'throttle' | 'debounce' | 'none';
    callback?: () => void;
}

const defaultOption = {
    wait: 333,
    type: 'debounce',
};

export default function useMeasure(option?: Option) {
    const { wait, type, callback } = { ...defaultOption, ...option };
    const targetRef = shallowRef<Element | HTMLElement>(null!);
    const rect = reactive<Rect>({ left: 0, top: 0, right: 0, bottom: 0, width: 0, height: 0, x: 0, y: 0 });

    let ro: ResizeObserver | null = null;
    const clearRo = () => {
        if (!ro) return;
        ro.disconnect();
        ro = null;
    };

    watchEffect(() => {
        if (!targetRef.value) return;
        clearRo();
        const observerFunc: ResizeObserverCallback = ([entry]) => {
            const domRect = entry.target.getBoundingClientRect();
            rect.left = domRect.left;
            rect.top = domRect.top;
            rect.right = domRect.right;
            rect.bottom = domRect.bottom;
            rect.width = domRect.width;
            rect.height = domRect.height;
            rect.x = domRect.x;
            rect.y = domRect.y;
            callback?.();
        };

        let execFunc = observerFunc;
        if (type === 'throttle' && wait >= 4) execFunc = throttle(execFunc, wait);
        else if (type === 'debounce' && wait >= 4) execFunc = debounce(execFunc, wait);
        ro = new ResizeObserver(execFunc);
        ro.observe(targetRef.value as Element);
    });

    onUnmounted(clearRo);

    return [targetRef, rect] as const;
}
